/* eslint-disable no-param-reassign */

import {Trans} from '@lingui/macro';
import produce, {castDraft} from 'immer';
import {flatMap} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined, OrNull} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {TagModel} from '../../../../models/api/tagModel';
import {decorateWithClientId} from '../../../editors/commons/clientIdModel';
import {Expression} from '../../../editors/expressions/expressionModel';
import {useExpressionsContext} from '../../../editors/expressions/expressionsContext';
import {TagListResult, TagResult} from '../../../editors/expressions/infer/expressionResultModel';
import {
  ANDConditions,
  isRemovedTagRuleCondition,
  RemovedTagIs,
  RuleConditionTypesEnum,
} from '../../ruleConditionModel';
import {TriggerWithConditions} from '../../ruleTriggerHelpers';
import {SelectedDropdownItem} from '../dropdowns/dropdownWithExpressions';
import {useOnSelectCreateExpressionRef} from '../hooks/useOnSelectCreateExpression';
import {TagsParameter} from '../parameters/tagParameter';
import {RuleMultilineParameterCard} from '../ruleMultilineParameterCard';
import {RuleTriggerIcon} from '../ruleTriggerIcon';

/*
 * Types.
 */

export interface RuleTagIsRemovedTriggerCardProps {
  triggerWithConditions: TriggerWithConditions;
  isDisabled?: boolean;
  errorMessage?: ReactNode;
  expressions: ReadonlyArray<Expression>;

  onUpdateConditions: HandlerOf<ANDConditions>;
  onDelete?: Handler;
}

/*
 * Styles.
 */

const StyledCardTitleSuffix = styled.span`
  margin-left: 5px;
  white-space: nowrap;
`;

/*
 * Component.
 */

export const RuleTagIsRemovedTriggerCard: FC<RuleTagIsRemovedTriggerCardProps> = (props) => {
  const {triggerWithConditions, isDisabled, errorMessage, onUpdateConditions, onDelete} = props;
  const {trigger, conditions} = triggerWithConditions;
  const {expressions} = useExpressionsContext();

  const currentValue = flatMap(conditions, (orConditions) =>
    flatMap(orConditions.filter(isRemovedTagRuleCondition), (condition) => condition.params.tag),
  ).filter(isDefined);

  const onChange = (updatedTagIds: OrNull<ReadonlyArray<SelectedDropdownItem<TagModel>>>) => {
    const updatedConditions = produce(conditions, (draft) => {
      // Clear every 'AND' node containing "removed tag is condition".
      draft.forEach((conds, i) => {
        if (conds.some(isRemovedTagRuleCondition)) {
          draft[i] = draft[i].filter((cond) => !isRemovedTagRuleCondition(cond));
        }
      });

      // All tags are selected <=> no "removed tag is" condition: bail out.
      if (updatedTagIds?.length === 0) {
        return;
      }

      // Add an 'AND' node with the selected tags.
      if (updatedTagIds) {
        draft.push(
          castDraft(
            updatedTagIds.map((tagId) =>
              castDraft(
                decorateWithClientId({
                  integration_id: 0,
                  type: RuleConditionTypesEnum.REMOVED_TAG_IS,
                  params: {
                    tag: tagId,
                  },
                } as RemovedTagIs),
              ),
            ),
          ),
        );
      }
    });

    onUpdateConditions(updatedConditions);
  };

  const onSelectCreateExpression = useOnSelectCreateExpressionRef(
    (expressionRef) => onChange([...currentValue, expressionRef]),
    [TagResult, TagListResult],
  );

  const title = (
    <>
      <TagsParameter
        selectedItems={currentValue}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={onChange}
        canSelectAnyItem
        onSelectCreateExpression={onSelectCreateExpression}
      />
      <StyledCardTitleSuffix>
        <Trans id="rule-editor-trigger-tag-removed-title">is removed</Trans>
      </StyledCardTitleSuffix>
    </>
  );

  return (
    <RuleMultilineParameterCard
      title={title}
      icon={<RuleTriggerIcon trigger={trigger} />}
      errorMessage={errorMessage}
      onCancel={onDelete}
    />
  );
};
